"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import {
	AlertTriangle,
	ArrowRightLeft,
	CheckCircle,
	Code,
	Copy,
	Download,
	Upload,
	X,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FileDropZone } from "@/components/FileDropZone";
import { useAccessibility } from "@/hooks/useAccessibility";

function uint8ArrayToBase64(bytes: Uint8Array): string {
	const chunkSize = 8192;
	let binary = "";
	for (let i = 0; i < bytes.length; i += chunkSize) {
		const chunk = bytes.subarray(i, i + chunkSize);
		binary += String.fromCharCode(...chunk);
	}
	return btoa(binary);
}

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const Base64Tool = () => {
	const t = useTranslations("base64Tool");
	const { announce } = useAccessibility();

	const [mode, setMode] = useState<"encode" | "decode">("encode");
	const [textInput, setTextInput] = useState("");
	const [output, setOutput] = useState("");
	const [copied, setCopied] = useState(false);
	const [statusMessage, setStatusMessage] = useState("");
	const [fileName, setFileName] = useState<string | null>(null);
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	const handleProcess = useCallback(() => {
		if (!textInput.trim()) return;

		if (mode === "encode") {
			const encoder = new TextEncoder();
			const bytes = encoder.encode(textInput);
			const base64 = uint8ArrayToBase64(bytes);
			setOutput(base64);
			setStatusMessage(t("status.encoded"));
			announce(t("a11y.encoded"));
		} else {
			try {
				const binary = atob(textInput.replace(/\s/g, ""));
				const bytes = new Uint8Array(binary.length);
				for (let i = 0; i < binary.length; i++)
					bytes[i] = binary.charCodeAt(i);
				const decoder = new TextDecoder();
				setOutput(decoder.decode(bytes));
				setStatusMessage(t("status.decoded"));
				announce(t("a11y.decoded"));
			} catch {
				setOutput("");
				setStatusMessage(t("status.invalidBase64"));
			}
		}
	}, [textInput, mode, t, announce]);

	const handleFileUpload = useCallback(
		(files: FileList | File[]) => {
			const file = files instanceof FileList ? files[0] : files[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = () => {
				const bytes = new Uint8Array(reader.result as ArrayBuffer);
				const base64 = uint8ArrayToBase64(bytes);
				setOutput(base64);
				setTextInput("");
				setFileName(file.name);
				setStatusMessage(
					t("status.fileEncoded", {
						name: file.name,
						size: formatFileSize(file.size),
					}),
				);
				announce(t("a11y.fileEncoded"));
			};
			reader.readAsArrayBuffer(file);
		},
		[t, announce],
	);

	const handleCopy = useCallback(async () => {
		try {
			await navigator.clipboard.writeText(output);
			setCopied(true);
			setStatusMessage(t("status.copied"));
			announce(t("a11y.copied"));
			setTimeout(() => setCopied(false), 2000);
		} catch {
			setStatusMessage(t("status.copyFailed"));
			announce(t("a11y.copyFailed"));
		}
	}, [output, t, announce]);

	const handleDownload = useCallback(() => {
		try {
			const binary = atob(output.replace(/\s/g, ""));
			const bytes = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++)
				bytes[i] = binary.charCodeAt(i);
			const blob = new Blob([bytes]);
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = "decoded-file";
			a.click();
			URL.revokeObjectURL(url);
			setStatusMessage(t("status.downloadReady"));
			announce(t("a11y.downloadStarted"));
		} catch {
			setStatusMessage(t("status.invalidBase64"));
		}
	}, [output, t, announce]);

	const handleClear = useCallback(() => {
		setTextInput("");
		setOutput("");
		setFileName(null);
		setStatusMessage("");
	}, []);

	const getStatusMessage = (): string => {
		return statusMessage || t("status.ready");
	};

	const hasOutput = output.length > 0;

	if (!mounted) {
		return (
			<div className="max-w-7xl mx-auto min-h-screen p-2 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Code
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>
				</div>
			</div>
		);
	}

	return (
		<ErrorBoundary>
			<div className="max-w-7xl mx-auto min-h-screen p-2 sm:p-8">
				<div className="mx-auto p-2 sm:p-6">
					<header className="text-center mb-10">
						<h1 className="text-3xl sm:text-4xl font-black flex items-center justify-center uppercase tracking-wide">
							<Code
								className="h-8 w-8 me-4 text-destructive"
								aria-hidden="true"
							/>
							{t("title")}
						</h1>
						<p className="mt-4 text-muted-foreground text-lg font-bold">
							{t("subtitle")}
						</p>
					</header>

					{/* Status Bar */}
					<Alert
						className={`mb-6 ${hasOutput ? "border-green-500 bg-green-50" : "border-yellow-500 bg-yellow-50"}`}
					>
						<AlertTriangle className="h-4 w-4" aria-hidden="true" />
						<AlertDescription className="font-bold">
							{getStatusMessage()}
						</AlertDescription>
					</Alert>

					<div className="flex flex-col gap-6">
						{/* Step 1: Mode Selection */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<ArrowRightLeft
										className="w-6 h-6"
										aria-hidden="true"
									/>
									{t("steps.mode")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								<Select
									value={mode}
									onValueChange={(v) => {
										setMode(v as "encode" | "decode");
										setOutput("");
										setStatusMessage("");
									}}
								>
									<SelectTrigger className="w-full">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="encode">
											{t("mode.encode")}
										</SelectItem>
										<SelectItem value="decode">
											{t("mode.decode")}
										</SelectItem>
									</SelectContent>
								</Select>
							</CardContent>
						</Card>

						{/* Step 2: Input */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Upload className="w-6 h-6" aria-hidden="true" />
									{t("steps.input")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0 space-y-4">
								<div>
									<label
										htmlFor="text-input"
										className="text-sm font-bold text-foreground"
									>
										{t("input.textLabel")}
									</label>
									<textarea
										id="text-input"
										value={textInput}
										onChange={(e) => setTextInput(e.target.value)}
										placeholder={t("input.textPlaceholder")}
										className="mt-2 w-full min-h-[200px] rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs outline-none font-mono focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30 resize-y"
									/>
									{textInput && (
										<div className="flex items-center justify-between mt-2">
											<Badge
												variant="secondary"
												className="text-xs font-mono"
											>
												{t("output.characterCount", {
													count: textInput.length,
												})}
											</Badge>
											<Button
												variant="ghost"
												size="sm"
												onClick={handleClear}
											>
												<X
													className="w-3 h-3 me-1"
													aria-hidden="true"
												/>
												{t("input.clear")}
											</Button>
										</div>
									)}
								</div>

								{mode === "encode" && (
									<div>
										<label className="text-sm font-bold text-foreground">
											{t("input.fileLabel")}
										</label>
										<div className="mt-2">
											<FileDropZone
												accept="*"
												multiple={false}
												onFiles={handleFileUpload}
												label={t("input.fileDropZone")}
												activeLabel={t("input.fileDragging")}
												ariaLabel={t("input.fileInputLabel")}
											/>
										</div>
										{fileName && (
											<p className="mt-2 text-sm text-muted-foreground font-bold">
												{fileName}
											</p>
										)}
									</div>
								)}
							</CardContent>
						</Card>

						{/* Process Button */}
						<div className="flex justify-center">
							<Button
								onClick={handleProcess}
								size="lg"
								variant={textInput.trim() ? "default" : "outline"}
								disabled={!textInput.trim()}
								className="px-8 py-4 text-lg"
							>
								<ArrowRightLeft
									className="w-5 h-5 me-3"
									aria-hidden="true"
								/>
								{mode === "encode"
									? t("mode.encode")
									: t("mode.decode")}
							</Button>
						</div>

						{/* Step 3: Output */}
						<Card className="p-3 sm:p-6">
							<CardHeader className="p-0 mb-4">
								<CardTitle className="flex items-center gap-3">
									<Code className="w-6 h-6" aria-hidden="true" />
									{t("steps.output")}
								</CardTitle>
							</CardHeader>
							<CardContent className="p-0">
								{hasOutput ? (
									<div className="space-y-4">
										<div className="p-4 bg-muted/50 rounded-md border max-h-80 overflow-y-auto">
											<p
												className="font-mono text-sm break-all whitespace-pre-wrap select-all"
												aria-live="polite"
											>
												{output}
											</p>
										</div>
										<div className="flex items-center justify-between">
											<Badge
												variant="secondary"
												className="text-xs font-mono"
											>
												{t("output.characterCount", {
													count: output.length,
												})}
											</Badge>
											<div className="flex gap-2">
												{mode === "decode" && (
													<Button
														onClick={handleDownload}
														variant="outline"
													>
														<Download
															className="w-4 h-4 me-2"
															aria-hidden="true"
														/>
														{t("output.download")}
													</Button>
												)}
												<Button
													onClick={handleCopy}
													variant={
														copied ? "default" : "outline"
													}
												>
													{copied ? (
														<CheckCircle
															className="w-4 h-4 me-2"
															aria-hidden="true"
														/>
													) : (
														<Copy
															className="w-4 h-4 me-2"
															aria-hidden="true"
														/>
													)}
													{copied
														? t("output.copied")
														: t("output.copy")}
												</Button>
											</div>
										</div>
									</div>
								) : (
									<div className="text-center min-h-[8rem] flex items-center justify-center">
										<p className="text-muted-foreground font-bold">
											{t("output.empty")}
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>

					{/* Privacy Notice */}
					<Alert className="mt-8">
						<AlertTriangle
							className="h-4 w-4 text-green-600"
							aria-hidden="true"
						/>
						<AlertDescription className="font-bold">
							{t("privacy")}
						</AlertDescription>
					</Alert>
				</div>
			</div>
		</ErrorBoundary>
	);
};

export default Base64Tool;
